import { splitEncoding } from '@contentful/live-preview';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { visuallyHidden } from '@mui/utils';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Variants,
} from 'framer-motion';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { TextEffectCF } from '@/types';

import { getHexValues } from './utils';

function Typewrite({
  text,
  color,
  prefersReducedMotion,
}: {
  text: string;
  color: string;
  prefersReducedMotion: boolean | null;
}) {
  const letters = Array.from(text);

  const cursorVariants: Variants = prefersReducedMotion
    ? { blinking: { opacity: 1 } }
    : {
        blinking: {
          opacity: [0, 1, 0],
          transition: {
            duration: 0.7,
            repeat: Infinity,
            ease: 'easeInOut',
          },
        },
      };

  const containerVariants: Variants = prefersReducedMotion
    ? { hidden: {}, visible: {} }
    : {
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.05,
          },
        },
      };

  const letterVariants: Variants = prefersReducedMotion
    ? {
        hidden: { opacity: 1 },
        visible: { opacity: 1 },
      }
    : {
        hidden: {
          opacity: 0,
          width: 0,
          overflow: 'hidden',
        },
        visible: {
          opacity: 1,
          width: 'auto',
          transition: {
            duration: 0.01,
          },
        },
      };

  return (
    <span style={{ color, display: 'flex', gap: '2px' }}>
      <motion.span
        style={{ display: 'flex' }}
        variants={containerVariants}
        initial='hidden'
        animate='visible'
      >
        {letters.map((letter, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
          <motion.span key={index} variants={letterVariants}>
            {/* Use a non-breaking space to maintain layout for spaces */}
            {letter === ' ' ? '\u00A0' : letter}
          </motion.span>
        ))}
      </motion.span>
      <motion.span variants={cursorVariants} animate='blinking'>
        |
      </motion.span>
    </span>
  );
}

export default function Typewriter({ entry }: { entry: TextEffectCF }) {
  const prefersReducedMotion = useReducedMotion();
  const [activeItem, setActiveItem] = useState(0);
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const { colors, text } = entry.fields;

  const visuallyHiddenList = useRef<HTMLSpanElement>(null);
  const colorHexValues = getHexValues(colors);
  const currentColor = colorHexValues[activeItem % colorHexValues.length];
  const textSegments = documentToPlainTextString(text, '\n').split('\n');

  useEffect(() => {
    if (textSegments.length <= 1) return;

    const currentText = splitEncoding(textSegments[activeItem]).cleaned;
    const typingDuration = currentText.length * 50 + 500;

    const timeoutId = setTimeout(() => {
      setActiveItem((current) => (current + 1) % textSegments.length);
    }, typingDuration);

    return () => clearTimeout(timeoutId);
  }, [activeItem, textSegments]);

  useLayoutEffect(() => {
    const calculateWidestElement = () => {
      requestAnimationFrame(() => {
        if (visuallyHiddenList.current) {
          let max = 0;

          Array.from(visuallyHiddenList.current.children).forEach((child) => {
            if (child instanceof HTMLElement) {
              max = Math.max(max, child.offsetWidth);
            }
          });

          setMaxWidth(max + 1);
        }
      });
    };

    calculateWidestElement();

    window.addEventListener('resize', calculateWidestElement);
    return () => window.removeEventListener('resize', calculateWidestElement);
  }, []);

  return (
    <Fragment>
      {/* This div is visually hidden. It's used for a11y and measuring the max width of longest element. */}
      <span style={visuallyHidden} ref={visuallyHiddenList}>
        {textSegments.map((component, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
          <span key={i}>
            {component} <span aria-hidden='true'> |</span>
          </span>
        ))}
      </span>
      <span
        aria-hidden='true'
        data-chromatic='ignore'
        style={{
          display: 'inline-block',
          overflow: 'hidden',
          minWidth: `${maxWidth}px`,
          verticalAlign: 'bottom',
        }}
      >
        <AnimatePresence mode='wait'>
          <Typewrite
            key={textSegments[activeItem]}
            text={textSegments[activeItem]}
            color={currentColor}
            prefersReducedMotion={prefersReducedMotion}
          />
        </AnimatePresence>
      </span>
    </Fragment>
  );
}
